/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import subHours from 'date-fns/subHours';
import addHours from 'date-fns/addHours';
import { formatDate } from '@angular/common';
import { format } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class TimeService {
  private localTimeZone: string =
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  private timeZoneOffSet = this.getTimeZoneOffset(
    new Date().toString(),
    'Europe/London',
  );
  private timeZoneOffSetUTC = this.getUTCTimeZoneOffsetFromLondon();

  public getTimeZoneOffset(date: string, timeZone: string): number {
    // Abuse the Intl API to get a local ISO 8601 string for a given time zone.
    const iso = new Date(date).toLocaleString('en-US', {
      timeZone: timeZone,
      hour12: true,
    });
    // Return the difference in timestamps, as hours
    // Positive values are West of GMT, opposite of ISO 8601
    // this matches the output of `Date.getTimeZoneOffset`
    return differenceInMinutes(new Date(date), new Date(iso)) / 60;
  }

  public getUTCTimeZoneOffset(): number {
    const date = new Date().setHours(0, 0, 0, 0);
    const iso = new Date(date).toLocaleString('en-US', {
      timeZone: 'UTC',
      hour12: false,
    });
    return differenceInMinutes(date, new Date(iso)) / 60;
  }

  public getUTCTimeZoneOffsetFromLondon(): number {
    const date = new Date().setHours(0, 0, 0, 0);

    const utc = new Date(date).toLocaleString('en-US', {
      timeZone: 'Europe/London',
      hour12: false,
    });

    const iso = new Date(date).toLocaleString('en-US', {
      timeZone: 'UTC',
      hour12: false,
    });

    return differenceInMinutes(new Date(utc), new Date(iso)) / 60;
  }

  public convertDateToOffSet(date: Date, timeZoneOffSet: number): Date {
    // If timeZoneOffSet is positive we need to subtract from current time
    // Else we want to add the hours to our current itme
    const functionNeeded: Function = timeZoneOffSet >= 0 ? subHours : addHours;
    return functionNeeded(date, timeZoneOffSet);
  }

  public formatDateTimeToFXHPESTGridDate(date: string): string {
    if (!date) {
      return '';
    }
    const dateConvertedToEEST = this.convertDateToOffSet(
      new Date(date),
      this.timeZoneOffSet,
    );
    return formatDate(
      dateConvertedToEEST,
      'MMM d, y, h:mm a',
      'en-US',
      this.localTimeZone,
    );
  }

  public formatDateTimeRollCalendar(date: string): string {
    if (!date) {
      return '';
    }

    const offset = date.indexOf('Z') > -1 ? 1 : 6;
    const dateWithoutTimezone = date.substring(0, date.length - offset);
    return formatDate(dateWithoutTimezone, 'MMM d, y, h:mm a', 'en-US');
  }

  public formatDisplayDateToServerAcceptableFormat(date: string): string {
    if (!date) {
      return '';
    }

    const formatedDate = new Date(new Date(date));
    const timeFormat =
      this.timeZoneOffSetUTC === 1
        ? "yyyy-MM-dd'T'HH:mm:ss+01:00"
        : "yyyy-MM-dd'T'HH:mm:ss'Z'";
    return format(formatedDate, timeFormat);
  }

  public formatValueDates(date: string) {
    const d = new Date(date).toISOString().substring(0, 10);
    return d.substring(0, 10);
  }

  public formatDisplayDateToServerNoUtcConversion(date: string): string {
    if (!date) {
      return '';
    }

    const formatedDate = new Date(new Date(date));
    // disabling becase the format requires single quotes
    // tslint:disable-next-line: quotemark
    return format(formatedDate, "yyyy-MM-dd'T'HH:mm:ss");
  }

  public getLocalTime(date: string): any {
    const offset = +this.getTimeZoneOffset(date, this.localTimeZone);
    return this.convertDateToOffSet(new Date(date), offset).toString();
  }
  // Retrieve today's date at 00:00:00:0000
  public getMinDate(): Date {
    const minDate = new Date();
    minDate.setHours(0, 0, 0, 0);
    return minDate;
  }

  public timeZoneAbbreviation(date: string): string {
    const tzAbbrev = new Date(date)
      .toLocaleTimeString('en-us', { timeZoneName: 'short' })
      .split(' ')[2];
    return tzAbbrev;
  }

  public toUnixTimestamp(date: string): number {
    return Math.floor(new Date(date).getTime() / 1000);
  }
}
